-- | Canonical browser route codec for the app.
-- |
-- | This module is the only place that parses browser location state into a
-- | route and renders a route back into a URL.
module App.Route
    ( Route(..)
    , HomeRoute
    , Step2ARoute
    , Step2BRoute
    , Step3Route
    , PreviewRoute
    , PreservedQuery(..)
    , SectionIntent(..)
    , parseRoute
    , routeToStep
    , routeTag
    , routeUrl
    , routePreservedQuery
    , routeForStepAndWorkQueueState
    , preserveQuery
    , preservedQueryParts
    , parseDocsQuery
    , parseFormsQuery
    , parseStudentsQuery
    , parseSectionHash
    , sectionHash
    , urlPrefix
    , hrUrlPrefix
    ) where

import Prelude

import API.FormBase (FormID)
import API.Types (DocumentID, StudentID)
import App.UrlPrefix as UrlPrefix
import Data.Array (catMaybes, filter)
import Data.Array as Arr
import Data.Int as Int
import Data.Maybe (Maybe(..), fromMaybe, maybe)
import Data.String as Str
import Data.String.CodeUnits as StrCU
import Data.String.Pattern (Pattern(..))
import Data.Tuple (Tuple(..))
import GraphQL.Type (innerId, typedId)
import Internal.SelectionMap as Sel
import Routing.PushState (LocationState)
import App.Step (Step(..))
import Steps.WorkQueue.Cache (getDocByStudent)
import Steps.WorkQueue.DocumentIndex (listDocs)
import Steps.WorkQueue.State (WorkQueueState)

newtype PreservedQuery = PreservedQuery (Array String)

data SectionIntent
    = SectionUnspecified
    | SectionClear
    | SectionSet String

type HomeRoute =
    { preservedQuery :: PreservedQuery
    }

type Step2ARoute =
    { preservedQuery :: PreservedQuery
    , selectedForms :: Array FormID
    }

type Step2BRoute =
    { preservedQuery :: PreservedQuery
    , selectedStudents :: Array StudentID
    , selectedForms :: Array FormID
    }

type Step3Route =
    { preservedQuery :: PreservedQuery
    , activeDoc :: Maybe DocumentID
    , docs :: Array DocumentID
    , section :: SectionIntent
    }

type PreviewRoute =
    { index :: Int
    , preservedQuery :: PreservedQuery
    }

data Route
    = RHome HomeRoute
    | RStep2A Step2ARoute
    | RStep2B Step2BRoute
    | RStep3 Step3Route
    | RPreview PreviewRoute
    | RTestForm PreservedQuery

routeTag :: Route -> String
routeTag = case _ of
    RHome _ -> "Home"
    RStep2A _ -> "Step2A"
    RStep2B _ -> "Step2B"
    RStep3 { activeDoc: Nothing } -> "Step3"
    RStep3 { activeDoc: Just docId } -> "Step3:" <> innerId docId
    RPreview { index } -> "Preview:" <> show index
    RTestForm _ -> "TestForm"

routeToStep :: Route -> Step
routeToStep = case _ of
    RHome _ -> Step1
    RStep2A _ -> Step2A
    RStep2B _ -> Step2B
    RStep3 { activeDoc: Nothing } -> Step3
    RStep3 { activeDoc: Just docId } -> Step3Doc docId
    RPreview { index } -> Preview index
    RTestForm _ -> TestForm

routePreservedQuery :: Route -> PreservedQuery
routePreservedQuery = case _ of
    RHome { preservedQuery } -> preservedQuery
    RStep2A { preservedQuery } -> preservedQuery
    RStep2B { preservedQuery } -> preservedQuery
    RStep3 { preservedQuery } -> preservedQuery
    RPreview { preservedQuery } -> preservedQuery
    RTestForm preservedQuery -> preservedQuery

preservedQueryParts :: PreservedQuery -> Array String
preservedQueryParts (PreservedQuery parts) = parts

routedParams :: Array String
routedParams = ["forms", "students", "docs"]

preserveQuery :: String -> PreservedQuery
preserveQuery search = PreservedQuery $ case StrCU.stripPrefix (Pattern "?") search of
    Nothing -> []
    Just query -> filter (not <<< isRoutedParam) $ Str.split (Pattern "&") query
  where
    isRoutedParam part = case Str.split (Pattern "=") part of
        [ key, _ ] -> key `Arr.elem` routedParams
        [ key ] -> key `Arr.elem` routedParams
        _ -> false

parseDocsQuery :: String -> Array DocumentID
parseDocsQuery = map typedId <<< parseQueryValues "docs"

parseFormsQuery :: String -> Array FormID
parseFormsQuery = map typedId <<< parseQueryValues "forms"

parseStudentsQuery :: String -> Array StudentID
parseStudentsQuery = map typedId <<< parseQueryValues "students"

parseRoute :: LocationState -> Route
parseRoute locationState =
    let preservedQuery = preserveQuery locationState.search
        docs = parseDocsQuery locationState.search
        forms = parseFormsQuery locationState.search
        students = parseStudentsQuery locationState.search
        section = case parseSectionHash locationState.hash of
            Just anchor -> SectionSet anchor
            Nothing -> SectionClear
        path = normalisedPath locationState.pathname
    in case path of
        "/step2" -> RStep2A { preservedQuery, selectedForms: forms }
        "/step2b" -> RStep2B { preservedQuery, selectedStudents: students, selectedForms: forms }
        "/step3" -> RStep3 { preservedQuery, activeDoc: Nothing, docs, section }
        "/test-form" -> RTestForm preservedQuery
        _ -> case step3DocPath path of
            Just docId -> RStep3 { preservedQuery, activeDoc: Just docId, docs: Arr.nub (Arr.cons docId docs), section }
            Nothing -> case previewPath path of
                Just index -> RPreview { index, preservedQuery }
                Nothing -> RHome { preservedQuery }

routeUrl :: Route -> String
routeUrl = case _ of
    RHome { preservedQuery } ->
        rootPath <> renderSearch preservedQuery [] <> ""
    RStep2A { preservedQuery, selectedForms } ->
        urlPrefix <> "/step2" <> renderSearch preservedQuery [ Tuple "forms" (map innerId selectedForms) ] <> ""
    RStep2B { preservedQuery, selectedStudents, selectedForms } ->
        urlPrefix <> "/step2b"
            <> renderSearch preservedQuery
                [ Tuple "students" (map innerId selectedStudents)
                , Tuple "forms" (map innerId selectedForms)
                ]
            <> ""
    RStep3 { preservedQuery, activeDoc, docs, section } ->
        let path = case activeDoc of
                Nothing -> urlPrefix <> "/step3"
                Just docId -> urlPrefix <> "/step3/" <> innerId docId
            otherDocs = case activeDoc of
                Nothing -> Arr.nub docs
                Just docId -> Arr.filter (_ /= docId) $ Arr.nub docs
            params = if Arr.null otherDocs
                then []
                else [ Tuple "docs" (map innerId otherDocs) ]
        in path <> renderSearch preservedQuery params <> renderHash section
    RPreview { index, preservedQuery } ->
        urlPrefix <> "/preview/" <> show index <> renderSearch preservedQuery [] <> ""
    RTestForm preservedQuery ->
        urlPrefix <> "/test-form" <> renderSearch preservedQuery [] <> ""

routeForStepAndWorkQueueState :: LocationState -> Step -> WorkQueueState -> Route
routeForStepAndWorkQueueState locationState step workQ =
    let preservedQuery = preserveQuery locationState.search
        selectedForms = map _.id $ Arr.fromFoldable $ Sel.listSelected workQ.forms
        selectedStudents = map _.id $ Arr.fromFoldable $ Sel.listSelected workQ.students
        editDocIds = Arr.nub $ map _.id workQ.editDocs
        loadedDocIds = Arr.nub $ map _.id $ Arr.fromFoldable $ listDocs workQ.documents
        routeDocIds = Arr.nub $ editDocIds <> loadedDocIds
        activeDoc = step3ActiveDocId workQ
    in case step of
        Step1 -> RHome { preservedQuery }
        Step2A -> RStep2A { preservedQuery, selectedForms }
        Step2B -> RStep2B { preservedQuery, selectedStudents, selectedForms }
        Step3 -> RStep3 { preservedQuery, activeDoc, docs: routeDocIds, section: SectionUnspecified }
        Step3Doc docId -> RStep3 { preservedQuery, activeDoc: Just docId, docs: Arr.nub (Arr.cons docId routeDocIds), section: SectionUnspecified }
        Preview index -> RPreview { index, preservedQuery }
        TestForm -> RTestForm preservedQuery
        Step4 -> RHome { preservedQuery }

parseSectionHash :: String -> Maybe String
parseSectionHash hash = case StrCU.stripPrefix (Pattern "#") hash of
    Just section | Str.null section -> Nothing
    Just section -> Just section
    Nothing | Str.null hash -> Nothing
    Nothing -> Just hash

sectionHash :: Maybe String -> String
sectionHash = maybe "" (renderHash <<< SectionSet)

renderHash :: SectionIntent -> String
renderHash = case _ of
    SectionSet section -> "#" <> section
    SectionClear -> ""
    SectionUnspecified -> ""

urlPrefix :: String
urlPrefix = case hrUrlPrefix of
    "" -> ""
    prefix -> "/" <> prefix

rootPath :: String
rootPath = case urlPrefix of
    "" -> "/"
    prefix -> prefix

parseQueryValues :: String -> String -> Array String
parseQueryValues key search = Arr.nub $ Arr.concatMap splitValues values
  where
    values = case StrCU.stripPrefix (Pattern "?") search of
        Nothing -> []
        Just query -> catMaybes $ map extractValue $ Str.split (Pattern "&") query

    extractValue part = case Str.split (Pattern "=") part of
        [ param, value ] | param == key -> Just value
        [ param ] | param == key -> Just ""
        _ -> Nothing

    splitValues value = Arr.filter (not <<< Str.null) $ map Str.trim $ Str.split (Pattern ",") value

renderSearch :: PreservedQuery -> Array (Tuple String (Array String)) -> String
renderSearch preserved routedParams' = case routedParts <> preservedParts of
    [] -> ""
    parts -> "?" <> Str.joinWith "&" parts
  where
    routedParts = catMaybes $ map renderParam routedParams'
    preservedParts = preservedQueryParts preserved

    renderParam (Tuple key values) = case Arr.filter (not <<< Str.null) values of
        [] -> Nothing
        vals -> Just $ key <> "=" <> Str.joinWith "," vals

normalisedPath :: String -> String
normalisedPath pathname =
    let stripped = case hrUrlPrefix of
            "" -> pathname
            prefix -> fromMaybe pathname $ StrCU.stripPrefix (Pattern ("/" <> prefix)) pathname
    in if Str.null stripped then "/" else stripped

hrUrlPrefix :: String
hrUrlPrefix = UrlPrefix.hrUrlPrefix

step3DocPath :: String -> Maybe DocumentID
step3DocPath path = do
    docPart <- StrCU.stripPrefix (Pattern "/step3/") path
    if Str.null docPart || Str.contains (Pattern "/") docPart
        then Nothing
        else Just $ typedId docPart

previewPath :: String -> Maybe Int
previewPath path = do
    indexPart <- StrCU.stripPrefix (Pattern "/preview/") path
    Int.fromString indexPart

step3ActiveDocId :: WorkQueueState -> Maybe DocumentID
step3ActiveDocId workQ = do
    lookup <- workQ.currentDoc
    case lookup.documentId of
        Just docId -> Just docId
        Nothing -> _.id <$> getDocByStudent workQ lookup
